import {
  Box,
  Button,
  Grid,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { algoliasearch, type SearchClient } from "algoliasearch";
import { useNavigate } from "react-router-dom";
import actions, { type DisplayFullOrganization } from "src/actions";
import { useDialog } from "src/Common/Dialog";
import { useFacebookMetaAdsConnect } from "src/Manager/hooks/useFacebookMetaAdsConnect";
import StatCard from "../Components/StatCard";
import { AlgoliaIndexType } from "common/interfaces/Algolia";
import type { CampaignIntent } from "common/interfaces/KYDMarketing";
import type { CampaignMetricsSnapshot } from "src/actions/ads";
import { orgMetricsToDisplay } from "./AdsEngagePage/utils";
import type { AdCampaignSortOption } from "./AdsEngagePage/utils";
import CampaignsTableAlgolia from "./AdsEngagePage/CampaignsTableAlgolia";
import CreateCampaignFlowModal, {
  type GuidedFlowDraft,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreateCampaignModal";
import GuidedCreateCampaignFlowScreen from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedCreateCampaignFlowScreen";
import { useManagedCampaigns } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/hooks/useManagedCampaigns";
import { launchCampaignWithPolling } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/guidedCampaignLaunch";
import { adsCampaignPublishSearch } from "src/Manager/Campaigns/Ads/CampaignDetail/useCampaignPublishPolling";
import type { GuidedCreateCampaignParams } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/guidedFlow/types";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import ProactiveInsightsSection from "./AdsEngagePage/ProactiveInsightsSection";
import { MetaAdsConnectionEmptyState } from "./AdsEngagePage/MetaAdsConnectionEmptyState";

export default function AdsEngagePage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [orgMetrics, setOrgMetrics] = useState<CampaignMetricsSnapshot | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [algoliaConfig, setAlgoliaConfig] = useState<{
    searchClient: SearchClient;
    indexName: string;
  } | null>(null);
  const [algoliaFailed, setAlgoliaFailed] = useState(false);
  const [sortOrderActive, setSortOrderActive] = useState<AdCampaignSortOption>(
    "updated_at_descending"
  );
  const [sortOrderPast, setSortOrderPast] = useState<AdCampaignSortOption>(
    "updated_at_descending"
  );
  const [metaConnectionStatus, setMetaConnectionStatus] = useState<
    "loading" | "connected" | "not_connected"
  >("loading");
  const [adAccountsStatus, setAdAccountsStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [adAccounts, setAdAccounts] = useState<{ id: string; name: string }[]>(
    []
  );
  const [guidedFlowDraft, setGuidedFlowDraft] =
    useState<GuidedFlowDraft | null>(null);
  const [isGuidedFlowModalOpen, setIsGuidedFlowModalOpen] = useState(false);
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [instagramAccounts, setInstagramAccounts] = useState<
    InstagramAccount[]
  >([]);
  const [pixels, setPixels] = useState<{ id: string; name: string }[]>([]);
  const [relatedDataLoading, setRelatedDataLoading] = useState(false);
  const [guidedPublishStatus, setGuidedPublishStatus] = useState<string | null>(
    null
  );
  const [suggestedIntents, setSuggestedIntents] = useState<CampaignIntent[]>(
    []
  );
  const [organization, setOrganization] =
    useState<DisplayFullOrganization | null>(null);
  const [metaConnectLoading, setMetaConnectLoading] = useState(false);

  const nowUnix = Math.floor(Date.now() / 1000);
  // First table: current/upcoming campaigns.
  const numericFiltersNotPast: (string | string[])[] = [
    ["schedule_end_at_unix = 0", `schedule_end_at_unix >= ${nowUnix}`],
  ];
  const numericFiltersPast: (string | string[])[] = [
    "schedule_end_at_unix > 0",
    `schedule_end_at_unix < ${nowUnix}`,
  ];

  const { pollForResult, searchInterests, searchGeoMarkets } =
    useManagedCampaigns({
      defaultCampaign: null,
      defaultEditingAdset: null,
      pages,
      instagramAccounts,
      onPollingComplete: () => {},
    });

  const refetchRelated = useCallback(async (ad_account_id: string) => {
    setRelatedDataLoading(true);
    try {
      await Promise.all([
        actions.ADMIN.listPages({ ad_account_id }).then(setPages),
        actions.ADMIN.listPixels({ ad_account_id }).then(setPixels),
        actions.ADMIN.listInstagramAccounts({ ad_account_id }).then(
          setInstagramAccounts
        ),
      ]);
    } catch (e) {
      console.error("Error fetching related data:", e);
    } finally {
      setRelatedDataLoading(false);
    }
  }, []);

  const refetchInstagramForPage = useCallback(
    (facebookPageId: string) => {
      if (!guidedFlowDraft?.adAccountId) return;
      setRelatedDataLoading(true);
      actions.ADMIN.listInstagramAccounts({
        ad_account_id: guidedFlowDraft.adAccountId,
        facebook_page_id: facebookPageId,
      })
        .then(setInstagramAccounts)
        .catch((e) => console.error("Error fetching Instagram accounts:", e))
        .finally(() => setRelatedDataLoading(false));
    },
    [guidedFlowDraft?.adAccountId]
  );

  const handleFlowCreateCampaign = useCallback(
    async (params: GuidedCreateCampaignParams) => {
      return await launchCampaignWithPolling(params, {
        toast,
        pollForResult,
        onStatusUpdate: setGuidedPublishStatus,
        onDone: () => setGuidedPublishStatus(null),
        awaitPolling: false,
      });
    },
    [pollForResult, toast]
  );

  const dialog = useDialog();

  const refetchOrganizationAndMetaState = useCallback(async () => {
    try {
      const org = await actions.ADMIN.fetchOrganization();
      setOrganization(org ?? null);
      const needsLogin = org?.ads_manager?.needs_login ?? true;
      setMetaConnectionStatus(needsLogin ? "not_connected" : "connected");

      if (org && !needsLogin) {
        setAdAccountsStatus("loading");
        try {
          const r = await actions.ADMIN.listAdAccounts?.();
          const list = r?.accounts ?? r ?? [];
          setAdAccounts(Array.isArray(list) ? list : []);
          setAdAccountsStatus("ready");
        } catch {
          setAdAccounts([]);
          setAdAccountsStatus("error");
        }
      } else {
        // No list attempt when disconnected or org missing; clear stale list/error
        setAdAccounts([]);
        setAdAccountsStatus("ready");
      }
    } catch {
      setOrganization(null);
      setMetaConnectionStatus("not_connected");
      setAdAccounts([]);
      setAdAccountsStatus("error");
      return;
    }
  }, []);

  const { triggerFacebookLogin } = useFacebookMetaAdsConnect({
    organization,
    toast,
    dialog,
    onAccessGranted: refetchOrganizationAndMetaState,
    onLoadingChange: setMetaConnectLoading,
  });

  useEffect(() => {
    void refetchOrganizationAndMetaState();
  }, [refetchOrganizationAndMetaState]);

  useEffect(() => {
    if (guidedFlowDraft?.adAccountId) {
      refetchRelated(guidedFlowDraft.adAccountId);
    }
  }, [guidedFlowDraft?.adAccountId, refetchRelated]);

  const refetchOrgMetrics = useCallback(async () => {
    try {
      const res = await actions.ADMIN.fetchOrganizationAdsMetrics();
      setOrgMetrics(res.metrics ?? null);
      setSuggestedIntents(res.suggested_intents ?? []);
    } catch {
      // keep previous state
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    actions.ADMIN.fetchOrganizationAdsMetrics()
      .then((res) => {
        if (cancelled) return;
        setOrgMetrics(res?.metrics ?? null);
        setSuggestedIntents(res?.suggested_intents ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (algoliaFailed) return;
    let cancelled = false;
    actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.AD_CAMPAIGNS,
    })
      .then((res) => {
        if (cancelled || !res?.events_config) return;
        const search = algoliasearch(
          res.events_config.app_id,
          res.events_config.api_key
        );
        setAlgoliaConfig({
          searchClient: search,
          indexName: res.events_config.index_name,
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setAlgoliaFailed(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [algoliaFailed]);

  const metricsFromOrg = useMemo(
    () => orgMetricsToDisplay(orgMetrics),
    [orgMetrics]
  );
  const metrics = useMemo(
    () =>
      metricsFromOrg ?? {
        displaySpend: "$0",
        displayRoas: "-",
        displayTickets: "0",
        displayCtr: "-",
      },
    [metricsFromOrg]
  );

  const shouldShowMetaAdsConnectionCta =
    metaConnectionStatus === "not_connected" ||
    (metaConnectionStatus === "connected" &&
      adAccountsStatus === "ready" &&
      adAccounts.length === 0);

  const isMetaAdsConfigLoading =
    metaConnectionStatus === "loading" ||
    (metaConnectionStatus === "connected" && adAccountsStatus === "loading");

  const hasAdAccountsError = adAccountsStatus === "error";

  if (isMetaAdsConfigLoading) {
    return (
      <VStack w="100%" py={12} spacing={3}>
        <Spinner />
        <Text color="gray.500" fontSize="sm">
          Loading Meta ads...
        </Text>
      </VStack>
    );
  }

  if (hasAdAccountsError) {
    return (
      <VStack w="100%" py={12} spacing={4}>
        <Text color="red.600" fontSize="md" fontWeight="500">
          Error loading ad accounts
        </Text>
        <Text color="gray.600" fontSize="sm">
          There was an error loading your Meta ad accounts. Please try again.
        </Text>
        <Button
          onClick={refetchOrganizationAndMetaState}
          colorScheme="gray"
          size="sm"
        >
          Retry
        </Button>
      </VStack>
    );
  }

  if (shouldShowMetaAdsConnectionCta) {
    const connectionEmptyReason =
      metaConnectionStatus === "not_connected"
        ? ("needs_login" as const)
        : ("no_ad_accounts" as const);
    const canTriggerMetaLoginFromAds =
      connectionEmptyReason === "needs_login" &&
      !!organization?.ads_manager?.config_id;
    return (
      <MetaAdsConnectionEmptyState
        reason={connectionEmptyReason}
        onConnectMetaAds={
          canTriggerMetaLoginFromAds ? triggerFacebookLogin : undefined
        }
        isConnectMetaAdsLoading={metaConnectLoading}
      />
    );
  }

  if (guidedFlowDraft) {
    return (
      <GuidedCreateCampaignFlowScreen
        draft={guidedFlowDraft}
        adAccounts={adAccounts}
        pages={pages}
        instagramAccounts={instagramAccounts}
        pixels={pixels}
        isRelatedDataLoading={relatedDataLoading}
        createUploadUrl={actions.ADMIN.createPublicUploadUrl}
        onBack={() => {
          setGuidedPublishStatus(null);
          setGuidedFlowDraft(null);
        }}
        onGuidedCreateAdAccountResolved={(ad_account_id) => {
          setGuidedFlowDraft((current) => {
            if (!current || current.mode !== "create") return current;
            if (current.adAccountId === ad_account_id) return current;
            return { ...current, adAccountId: ad_account_id };
          });
        }}
        onLaunchSuccess={(result) => {
          if (result?.campaignId) {
            navigate(
              `/campaigns/ads/${result.campaignId}${adsCampaignPublishSearch(
                result.publishUpdateId
              )}`
            );
            return;
          }
          setGuidedPublishStatus(null);
          setGuidedFlowDraft(null);
        }}
        toast={toast}
        onRefetchInstagramForPage={refetchInstagramForPage}
        handleCreateCampaign={handleFlowCreateCampaign}
        publishStatusText={guidedPublishStatus}
        search={{
          interests: searchInterests,
          geo_markets: searchGeoMarkets,
        }}
      />
    );
  }

  const shouldShowOrgAdsMetrics =
    orgMetrics &&
    ((orgMetrics.spend ?? 0) > 0 ||
      (orgMetrics.roas ?? 0) > 0 ||
      (orgMetrics.conversions ?? 0) > 0 ||
      (orgMetrics.ctr ?? 0) > 0);

  return (
    <VStack w="100%" align="stretch" spacing={6} py={4}>
      <HStack justifyContent="space-between" w="100%" flexWrap="wrap" gap={3}>
        <Text fontSize="32px" fontWeight="400">
          Meta Campaigns
        </Text>
        <Button
          leftIcon={<FiPlus />}
          size="md"
          bg="black"
          color="white"
          borderRadius="full"
          _hover={{ bg: "gray.800" }}
          onClick={() => setIsGuidedFlowModalOpen(true)}
        >
          New Ad Campaign
        </Button>
      </HStack>

      {shouldShowOrgAdsMetrics && (
        <Grid
          templateColumns={{
            base: "1fr 1fr",
            md: "repeat(4, 1fr)",
          }}
          gap={4}
        >
          <StatCard
            title="Spend (last 30 days)"
            value={metrics.displaySpend}
            valueFontSize="20px"
          />
          <StatCard
            title="Avg. ROAS"
            value={metrics.displayRoas}
            valueFontSize="20px"
          />
          <StatCard
            title="Checkouts"
            value={metrics.displayTickets}
            valueFontSize="20px"
            isKYD
            highlight
          />
          <StatCard
            title="Avg. CTR"
            value={metrics.displayCtr}
            valueFontSize="20px"
          />
        </Grid>
      )}

      {/* <ProactiveInsightsSection
        intents={suggestedIntents}
        onRefetch={refetchOrgMetrics}
      /> */}

      <Box>
        {loading ? (
          <VStack py={6}>
            <Spinner />
          </VStack>
        ) : algoliaFailed ? (
          <Box
            p={4}
            borderWidth="1px"
            borderRadius="md"
            borderColor="red.200"
            bg="red.50"
          >
            <Text color="red.700" fontSize="sm">
              Search is temporarily unavailable. Please try again later.
            </Text>
          </Box>
        ) : !algoliaConfig ? (
          <VStack py={6}>
            <Spinner />
            <Text color="gray.500" fontSize="sm">
              Setting up search…
            </Text>
          </VStack>
        ) : (
          <CampaignsTableAlgolia
            searchClient={algoliaConfig.searchClient}
            indexName={algoliaConfig.indexName}
            title="Current & upcoming campaigns"
            sortOrder={sortOrderActive}
            onSortChange={setSortOrderActive}
            numericFilters={numericFiltersNotPast}
          />
        )}
      </Box>

      <Box>
        {loading || algoliaFailed || !algoliaConfig ? (
          <Text color="gray.500" fontSize="sm" py={2}>
            {algoliaFailed
              ? "Search unavailable."
              : !algoliaConfig
              ? "Setting up search…"
              : "Loading…"}
          </Text>
        ) : (
          <CampaignsTableAlgolia
            searchClient={algoliaConfig.searchClient}
            indexName={algoliaConfig.indexName}
            title="Past Campaigns"
            sortOrder={sortOrderPast}
            onSortChange={setSortOrderPast}
            numericFilters={numericFiltersPast}
            showPerformanceIndicator={false}
            showLegend={false}
          />
        )}
      </Box>
      <CreateCampaignFlowModal
        isOpen={isGuidedFlowModalOpen}
        onClose={() => setIsGuidedFlowModalOpen(false)}
        adAccounts={adAccounts}
        onDraftReady={(draft) => {
          setGuidedFlowDraft(draft);
          setIsGuidedFlowModalOpen(false);
        }}
      />
    </VStack>
  );
}
